(() => {
  "use strict";

  const ARTICLE_SELECTOR = "[data-hass-article]";
  const CTA_PATTERN = /^\s*\[cta:\s*([^\]]+?)\s*\]\s*$/i;
  const CTA_INLINE_PATTERN = /\[cta:\s*([^\]]+?)\s*\]/i;
  const productCardCache = new Map();

  function slugifyHeading(value) {
    const normalized = value.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

    try {
      return normalized.replace(/[^\p{Letter}\p{Number}]+/gu, "-").replace(/^-+|-+$/g, "");
    } catch (_error) {
      return normalized.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    }
  }

  function assignHeadingIds(headings) {
    const usedIds = new Set(Array.from(document.querySelectorAll("[id]"), (element) => element.id));

    headings.forEach((heading, index) => {
      if (heading.id) return;

      const baseId = slugifyHeading(heading.textContent || "") || `article-section-${index + 1}`;
      let nextId = baseId;
      let suffix = 2;

      while (usedIds.has(nextId)) {
        nextId = `${baseId}-${suffix}`;
        suffix += 1;
      }

      heading.id = nextId;
      usedIds.add(nextId);
    });
  }

  function enhanceIntro(content) {
    const firstParagraph = Array.from(content.querySelectorAll("p")).find((paragraph) => {
      if (paragraph.querySelector("img, table")) return false;
      if (paragraph.closest("li, blockquote, [data-hass-article-product]")) return false;
      return Boolean(paragraph.textContent.trim()) && !CTA_PATTERN.test(paragraph.textContent);
    });

    if (firstParagraph) firstParagraph.classList.add("hass-article__intro");
  }

  function enhanceLists(content) {
    content.querySelectorAll("ul, ol").forEach((list) => {
      if (list.closest("li, .hass-article-faq__answer, [data-hass-article-product]")) return;
      list.classList.add("hass-article-list");
    });
  }

  function enhanceMedia(content) {
    content.querySelectorAll("figure").forEach((figure) => figure.classList.add("hass-article__media"));

    content.querySelectorAll("p").forEach((paragraph) => {
      if (!paragraph.querySelector("img")) return;
      if (paragraph.textContent.trim()) return;
      paragraph.classList.add("hass-article__media");
    });

    content.querySelectorAll("img").forEach((image) => {
      if (image.closest("figure, p.hass-article__media, [data-hass-article-product]")) return;

      const mediaBlock = image.closest(".ei") || image.closest(".lazyload-wrapper");
      if (!mediaBlock || !content.contains(mediaBlock)) return;
      mediaBlock.classList.add("hass-article__media");
    });
  }

  function promoteFirstTableRow(table) {
    if (table.tHead) return;

    const firstRow = table.rows[0];
    if (!firstRow) return;

    const cells = Array.from(firstRow.cells);
    if (!cells.length || cells.some((cell) => cell.tagName === "TH")) return;

    cells.forEach((cell) => {
      const headerCell = document.createElement("th");
      Array.from(cell.attributes).forEach((attribute) => {
        headerCell.setAttribute(attribute.name, attribute.value);
      });
      headerCell.setAttribute("scope", "col");

      while (cell.firstChild) headerCell.appendChild(cell.firstChild);
      cell.replaceWith(headerCell);
    });

    table.createTHead().appendChild(firstRow);
  }

  function enhanceTables(root, content) {
    content.querySelectorAll("table").forEach((table) => {
      if (table.parentElement?.classList.contains("hass-article-table")) return;

      promoteFirstTableRow(table);

      const wrapper = document.createElement("div");
      wrapper.className = "hass-article-table";
      wrapper.setAttribute("role", "region");
      wrapper.setAttribute("tabindex", "0");
      wrapper.setAttribute("aria-label", table.getAttribute("aria-label") || root.dataset.tableLabel || "");
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  function isFaqHeading(heading) {
    const value = (heading.textContent || "").trim();
    return [
      /\bfaq(?:s)?\b/i,
      /frequently asked questions?/i,
      /common questions?/i,
      /常见问题|常見問題/,
      /よくある質問/,
      /자주 묻는 질문/,
      /häufig gestellte fragen/i,
      /questions (?:fréquentes|fréquemment posées)/i,
      /domande frequenti/i,
      /preguntas frecuentes/i,
      /perguntas frequentes/i,
    ].some((pattern) => pattern.test(value));
  }

  function buildFaq(root, content) {
    const faqHeading = Array.from(content.querySelectorAll("h2")).find(isFaqHeading);
    if (!faqHeading || !faqHeading.parentNode) return;

    const questions = [];
    let cursor = faqHeading.nextElementSibling;

    while (cursor && cursor.tagName !== "H2") {
      if (cursor.tagName === "H3") questions.push(cursor);
      cursor = cursor.nextElementSibling;
    }

    if (!questions.length) return;

    const faqList = document.createElement("div");
    faqList.className = "hass-article-faq__list";
    faqHeading.parentNode.insertBefore(faqList, questions[0]);

    const schemaEntries = [];
    const detailsItems = [];

    questions.forEach((question) => {
      const details = document.createElement("details");
      const summary = document.createElement("summary");
      const answer = document.createElement("div");
      const questionText = question.textContent.trim();
      let answerCursor = question.nextSibling;

      details.className = "hass-article-faq__item";
      answer.className = "hass-article-faq__answer";
      question.parentNode.removeChild(question);
      summary.appendChild(question);
      details.appendChild(summary);

      while (answerCursor) {
        const nextNode = answerCursor.nextSibling;
        if (answerCursor.nodeType === Node.ELEMENT_NODE && ["H2", "H3"].includes(answerCursor.tagName)) break;
        answer.appendChild(answerCursor);
        answerCursor = nextNode;
      }

      details.appendChild(answer);
      faqList.appendChild(details);
      detailsItems.push(details);

      const answerText = answer.textContent.replace(/\s+/g, " ").trim();
      if (questionText && answerText) {
        schemaEntries.push({
          "@type": "Question",
          name: questionText,
          acceptedAnswer: {
            "@type": "Answer",
            text: answerText,
          },
        });
      }
    });

    detailsItems.forEach((details) => {
      details.addEventListener("toggle", () => {
        if (!details.open) return;
        detailsItems.forEach((otherDetails) => {
          if (otherDetails !== details) otherDetails.open = false;
        });
      });
    });

    if (schemaEntries.length) {
      const faqSchema = document.createElement("script");
      faqSchema.type = "application/ld+json";
      faqSchema.dataset.hassFaqSchema = "";
      faqSchema.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: schemaEntries,
      });
      root.appendChild(faqSchema);
    }
  }

  function buildToc(root, content) {
    const headings = Array.from(content.querySelectorAll("h2")).filter(
      (heading) => !heading.closest("[data-hass-article-product]"),
    );

    if (!headings.length) return;
    assignHeadingIds(headings);

    const tocContainers = root.querySelectorAll("[data-article-toc-desktop], [data-article-toc-mobile]");

    tocContainers.forEach((container) => {
      const list = container.querySelector("[data-article-toc-list]");
      if (!list) return;

      headings.forEach((heading) => {
        const item = document.createElement("li");
        const link = document.createElement("a");
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        item.appendChild(link);
        list.appendChild(item);

        link.addEventListener("click", () => {
          beginTocNavigation(heading.id);
          const mobileToc = link.closest("details");
          if (mobileToc) mobileToc.open = false;
        });
      });

      container.hidden = false;
    });

    const tocLinks = Array.from(root.querySelectorAll("[data-article-toc-desktop] a, [data-article-toc-mobile] a"));
    if (!tocLinks.length) return;

    const setActiveHeading = (headingId) => {
      tocLinks.forEach((link) => {
        const isActive = link.getAttribute("href") === `#${headingId}`;
        link.classList.toggle("is-active", isActive);
        if (isActive) link.setAttribute("aria-current", "location");
        else link.removeAttribute("aria-current");
      });
    };

    let navigationHeadingId = "";
    let navigationEndTimer = 0;

    const scheduleNavigationEnd = () => {
      if (navigationEndTimer) window.clearTimeout(navigationEndTimer);
      navigationEndTimer = window.setTimeout(() => {
        navigationEndTimer = 0;
        navigationHeadingId = "";
        updateActiveHeading();
      }, 180);
    };

    const beginTocNavigation = (headingId) => {
      navigationHeadingId = headingId;
      setActiveHeading(headingId);
      scheduleNavigationEnd();
    };

    const updateActiveHeading = () => {
      const scrollMargin = Number.parseFloat(window.getComputedStyle(headings[0]).scrollMarginTop);
      const activationOffset = Number.isFinite(scrollMargin) ? scrollMargin + 1 : 111;
      let activeHeading = headings[0];

      headings.forEach((heading) => {
        if (heading.getBoundingClientRect().top <= activationOffset) activeHeading = heading;
      });

      const isAtPageEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (isAtPageEnd) activeHeading = headings[headings.length - 1];

      setActiveHeading(activeHeading.id);
    };

    let activeFrame = 0;
    const queueActiveUpdate = () => {
      if (navigationHeadingId) {
        setActiveHeading(navigationHeadingId);
        scheduleNavigationEnd();
        return;
      }

      if (activeFrame) return;
      activeFrame = window.requestAnimationFrame(() => {
        activeFrame = 0;
        updateActiveHeading();
      });
    };

    const cleanupTocTracking = () => {
      if (activeFrame) window.cancelAnimationFrame(activeFrame);
      if (navigationEndTimer) window.clearTimeout(navigationEndTimer);
      window.removeEventListener("scroll", queueActiveUpdate);
      window.removeEventListener("resize", queueActiveUpdate);
      window.removeEventListener("load", queueActiveUpdate);
      document.removeEventListener("shopify:section:unload", handleSectionUnload);
    };

    const handleSectionUnload = (event) => {
      if (event.target === root || event.target.contains(root)) cleanupTocTracking();
    };

    window.addEventListener("scroll", queueActiveUpdate, { passive: true });
    window.addEventListener("resize", queueActiveUpdate, { passive: true });
    window.addEventListener("load", queueActiveUpdate, { once: true });
    document.addEventListener("shopify:section:unload", handleSectionUnload);
    updateActiveHeading();
  }

  function extractProductHandle(rawUrl) {
    try {
      const url = new URL(rawUrl.trim(), window.location.origin);
      const match = decodeURIComponent(url.pathname).match(/\/products\/([^/?#]+)/i);
      return match ? match[1] : "";
    } catch (_error) {
      return "";
    }
  }

  function localizedProductUrl(handle, includeView) {
    const rootPath = window.Shopify?.routes?.root || "/";
    const normalizedRoot = rootPath.endsWith("/") ? rootPath : `${rootPath}/`;
    const url = new URL(`${normalizedRoot}products/${encodeURIComponent(handle)}`, window.location.origin);
    if (includeView) url.searchParams.set("view", "article-card");
    return url;
  }

  async function fetchProductCard(handle) {
    if (!productCardCache.has(handle)) {
      const request = fetch(localizedProductUrl(handle, true), {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Product card request failed with ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, "text/html");
          return parsed.querySelector("[data-hass-article-product]");
        })
        .catch(() => null);

      productCardCache.set(handle, request);
    }

    return productCardCache.get(handle);
  }

  function createProductSlot(root, rawUrl) {
    const slot = document.createElement("div");
    slot.className = "hass-article-product-slot";
    slot.setAttribute("aria-live", "polite");
    slot.setAttribute("aria-busy", "true");
    slot.textContent = root.dataset.productLoading || "";
    slot.dataset.productUrl = rawUrl.trim();
    return slot;
  }

  function collectCtaSlots(root, content) {
    const slots = [];

    content.querySelectorAll("p").forEach((paragraph) => {
      const match = paragraph.textContent.match(CTA_PATTERN);
      if (!match) return;
      const slot = createProductSlot(root, match[1]);
      paragraph.replaceWith(slot);
      slots.push(slot);
    });

    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || parent.closest("a, code, pre, script, style, .hass-article-product-slot")) {
          return NodeFilter.FILTER_REJECT;
        }
        return CTA_INLINE_PATTERN.test(node.nodeValue || "") ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((textNode) => {
      const match = (textNode.nodeValue || "").match(CTA_INLINE_PATTERN);
      if (!match) return;

      textNode.nodeValue = (textNode.nodeValue || "").replace(match[0], "");
      let anchor = textNode.parentElement?.closest("p, li, blockquote") || textNode.parentElement;
      const containingList = anchor?.closest("li")?.closest("ul, ol");
      if (containingList) anchor = containingList;
      if (!anchor?.parentNode) return;

      const slot = createProductSlot(root, match[1]);
      anchor.parentNode.insertBefore(slot, anchor.nextSibling);
      slots.push(slot);
    });

    return slots;
  }

  async function renderProductCards(root, content) {
    const slots = collectCtaSlots(root, content);

    await Promise.all(
      slots.map(async (slot) => {
        const handle = extractProductHandle(slot.dataset.productUrl || "");
        const productUrl = handle ? localizedProductUrl(handle, false) : null;
        const card = handle ? await fetchProductCard(handle) : null;

        if (card) {
          slot.replaceWith(card.cloneNode(true));
          return;
        }

        slot.removeAttribute("aria-busy");
        slot.textContent = "";
        if (!productUrl) return;

        const fallbackLink = document.createElement("a");
        fallbackLink.className = "hass-article-product-fallback";
        fallbackLink.href = productUrl.toString();
        fallbackLink.textContent = root.dataset.productUnavailable || "";
        slot.appendChild(fallbackLink);
      }),
    );
  }

  function initializeArticle(root) {
    if (!root || root.dataset.articleReady === "true") return;
    const content = root.querySelector("[data-article-content]");
    if (!content) return;

    root.dataset.articleReady = "true";
    enhanceIntro(content);
    enhanceLists(content);
    enhanceMedia(content);
    enhanceTables(root, content);
    buildFaq(root, content);
    buildToc(root, content);
    renderProductCards(root, content);
  }

  function initializeAll(scope = document) {
    if (scope.matches?.(ARTICLE_SELECTOR)) initializeArticle(scope);
    scope.querySelectorAll?.(ARTICLE_SELECTOR).forEach(initializeArticle);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
})();
